import type { PostHog } from 'posthog-js';
import { useFeatureFlagEnabled } from 'posthog-js/react';

import { STARTER_MONTHLY_SEARCHES } from '@/constants/plan-cards-v4';
import { bestEffortAnalytics } from '@/lib/best-effort-analytics';
import type {
  AppSumoUpgradeGain,
  AppSumoUpgradeGainRow,
  AppSumoUpgradeOffer,
  AppSumoUpgradeOfferPreview,
  AppSumoUpgradePhase,
  AppSumoUpgradePlan,
  AppSumoUpgradePlanKey,
} from '@/store/features/appsumo.feature';
import { BRAND } from '@/lib/brand';

/**
 * The October 2026 AppSumo upgrade campaign: AppSumo-only workspaces add
 * Cleanlist Starter or Cleanlist Pro on top of their lifetime deal at the
 * AppSumo price ($19 or $39 a seat per month, monthly only; Starter has 250
 * searches a month and Pro no limit), until Nov 13.
 *
 * The backend owns every number (`GET /appsumo/upgrade/offer`): eligibility,
 * phase, both plans' prices, what each plan adds for this tier. This module
 * turns that offer
 * into the words of the copy deck (tasks/appsumo-oct2026/COPY.md in the
 * landing-page repo), so every customer-facing string of the campaign lives
 * here, in one place to compare against the deck.
 */

// ---------------------------------------------------------------------------
// Flags
// ---------------------------------------------------------------------------

/** Gates every campaign surface. The backend gates the offer and the
 *  checkout on the same key, so both sides toggle together. */
export const APPSUMO_UPGRADE_FLAG = 'appsumo_upgrade_v1';

/** The 7-day trial fallback. Read by the backend only; the portal follows
 *  `offer.trial.available`, so the two can never disagree. */
export const APPSUMO_UPGRADE_TRIAL_FLAG = 'appsumo_upgrade_trial_v1';

export const APPSUMO_UPGRADE_PATH = '/appsumo/upgrade';

/**
 * Local-dev override: on the dev server only (never a test run, never a build),
 * `localStorage['cl:flag:appsumo_upgrade_v1']` answers ahead of PostHog,
 * `'1'` on and `'0'` off. Read per call so a test can stub the mode.
 */
function devFlagOverride(key: string): boolean | null {
  if (import.meta.env.MODE !== 'development') return null;
  try {
    const raw = localStorage.getItem(`cl:flag:${key}`);
    if (raw === '1') return true;
    if (raw === '0') return false;
    return null;
  } catch {
    return null;
  }
}

/** The campaign switch, fail closed: undefined while PostHog loads reads as
 *  off, so a dark-launched surface never flashes. */
export function useAppSumoUpgradeFlag(): boolean {
  const enabled = useFeatureFlagEnabled(APPSUMO_UPGRADE_FLAG) === true;
  return devFlagOverride(APPSUMO_UPGRADE_FLAG) ?? enabled;
}

// ---------------------------------------------------------------------------
// Money, dates
// ---------------------------------------------------------------------------

/** "$53.40", "$1,929.60"; a whole amount drops its cents ("$89"). */
export function formatCents(cents: number): string {
  const whole = cents % 100 === 0;
  return `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  })}`;
}

/** "1,500" */
export const formatCount = (count: number) => count.toLocaleString('en-US');

/** The deadline is midnight in New York, so every date is read there. */
const DEADLINE_TIME_ZONE = 'America/New_York';

// Newer ICU puts a narrow no-break space before AM/PM. The copy deck has a
// plain space, and so does every test.
const plainSpaces = (text: string) => text.replace(/[\u202f\u00a0]/g, ' ');

function inDeadlineZone(
  date: Date,
  options: Intl.DateTimeFormatOptions
): string {
  return plainSpaces(
    new Intl.DateTimeFormat('en-US', {
      timeZone: DEADLINE_TIME_ZONE,
      ...options,
    }).format(date)
  );
}

export interface DeadlineLabels {
  /** "Nov 13" */
  short: string;
  /** "November 13" */
  long: string;
  /** "Friday" */
  weekday: string;
  /** "11:59 PM" */
  time: string;
}

export function deadlineLabels(endsAt: string): DeadlineLabels {
  const date = new Date(endsAt);
  return {
    short: inDeadlineZone(date, { month: 'short', day: 'numeric' }),
    long: inDeadlineZone(date, { month: 'long', day: 'numeric' }),
    weekday: inDeadlineZone(date, { weekday: 'long' }),
    time: inDeadlineZone(date, { hour: 'numeric', minute: '2-digit' }),
  };
}

/** Midnight UTC of the New York calendar day `date` falls on. */
function deadlineZoneDay(date: Date): number {
  const [year, month, day] = new Intl.DateTimeFormat('en-CA', {
    timeZone: DEADLINE_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
    .format(date)
    .split('-')
    .map(Number);
  return Date.UTC(year, month - 1, day);
}

/**
 * Calendar days, in New York, from today to the deadline's day: 0 on the
 * last day, 1 the day before. Counting calendar days rather than rounding
 * hours up keeps "tomorrow" and "tonight" true at any hour.
 */
export function calendarDaysUntil(endsAt: string, now: number): number {
  return Math.round(
    (deadlineZoneDay(new Date(endsAt)) - deadlineZoneDay(new Date(now))) /
      86_400_000
  );
}

/** "November 21": the day a trial started now first charges, on the
 *  customer's own calendar. */
export function trialChargeDate(days: number, now: number): string {
  return plainSpaces(
    new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric' }).format(
      new Date(now + days * 86_400_000)
    )
  );
}

// ---------------------------------------------------------------------------
// Dev preview (`?previewTier=5&previewPhase=final_week`, DEV builds only)
// ---------------------------------------------------------------------------

/** `starter` and `pro`: stacked at the AppSumo price (with `previewPhase`,
 *  the move from Starter to Pro). */
export type AppSumoPreviewState =
  | 'eligible'
  | 'subscribed'
  | 'closed'
  | 'starter'
  | 'pro';

/** Every dev preview param, as the URL spells them. */
export const APPSUMO_PREVIEW_PARAM_KEYS = [
  'previewTier',
  'previewState',
  'previewPhase',
  'previewMembers',
  'previewTrial',
] as const;

/**
 * Renders the campaign as an AppSumo org on that tier, state and phase would
 * see it. The SERVER synthesizes the entitlements and runs the real resolver
 * (it refuses these in production), so a review exercises the real offer.
 * Numbers, not strings: TanStack writes them back into the URL as they came.
 */
export type AppSumoPreviewParams = {
  previewTier?: number;
  previewState?: AppSumoPreviewState;
  previewPhase?: AppSumoUpgradePhase;
  previewMembers?: number;
  previewTrial?: 1;
};

const PREVIEW_STATES: readonly AppSumoPreviewState[] = [
  'eligible',
  'subscribed',
  'closed',
  'starter',
  'pro',
];
const PREVIEW_PHASES: readonly AppSumoUpgradePhase[] = [
  'open',
  'final_week',
  'closed',
];

function intInRange(raw: unknown, min: number, max: number) {
  const n = typeof raw === 'string' ? Number(raw) : raw;
  return typeof n === 'number' && Number.isInteger(n) && n >= min && n <= max
    ? n
    : undefined;
}

function oneOf<T extends string>(raw: unknown, values: readonly T[]) {
  return values.find((value) => value === raw);
}

/** The valid preview params in `source` (URL search or a router search). */
export function parseAppSumoPreviewParams(
  source: Record<string, unknown>
): AppSumoPreviewParams {
  const params: AppSumoPreviewParams = {};
  const tier = intInRange(source.previewTier, 1, 7);
  if (tier !== undefined) params.previewTier = tier;
  const state = oneOf(source.previewState, PREVIEW_STATES);
  if (state) params.previewState = state;
  const phase = oneOf(source.previewPhase, PREVIEW_PHASES);
  if (phase) params.previewPhase = phase;
  const members = intInRange(source.previewMembers, 1, 50);
  if (members !== undefined) params.previewMembers = members;
  if (String(source.previewTrial) === '1') params.previewTrial = 1;
  return params;
}

/** The preview params on the current URL; always empty outside DEV. */
export function readAppSumoPreviewParams(): AppSumoPreviewParams {
  if (!import.meta.env.DEV || typeof window === 'undefined') return {};
  return parseAppSumoPreviewParams(
    Object.fromEntries(new URLSearchParams(window.location.search))
  );
}

export const hasAppSumoPreviewParams = (params: AppSumoPreviewParams) =>
  Object.keys(params).length > 0;

/** The API's names for the same params. */
export function toOfferPreview(
  params: AppSumoPreviewParams
): AppSumoUpgradeOfferPreview | undefined {
  if (!hasAppSumoPreviewParams(params)) return undefined;
  return {
    preview_tier: params.previewTier,
    preview_state: params.previewState,
    preview_phase: params.previewPhase,
    preview_members: params.previewMembers,
    preview_trial: params.previewTrial ? true : undefined,
  };
}

// ---------------------------------------------------------------------------
// Links to the upgrade page
// ---------------------------------------------------------------------------

/** Where someone came to the upgrade page from. */
export type AppSumoUpgradeSource =
  | 'banner'
  | 'gate'
  | 'plans'
  | 'whats_new'
  | 'home';

export type AppSumoCheckoutStatus = 'success' | 'trial_started' | 'cancelled';

/** The upgrade page's search params. It sends everyone on to the plan
 *  wizard (with `plan` preselected) but for a return from Stripe. */
export type AppSumoUpgradeSearch = AppSumoPreviewParams & {
  plan?: AppSumoUpgradePlanKey;
  source?: string;
  feature?: string;
  status?: AppSumoCheckoutStatus;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
};

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

const STATUSES: readonly AppSumoCheckoutStatus[] = [
  'success',
  'trial_started',
  'cancelled',
];

const PLAN_PARAMS: readonly AppSumoUpgradePlanKey[] = ['starter', 'pro'];

const shortText = (raw: unknown) =>
  typeof raw === 'string' && raw.trim() ? raw.trim().slice(0, 100) : undefined;

/** The route's `validateSearch`. Preview params survive in DEV builds only. */
export function validateAppSumoUpgradeSearch(
  search: Record<string, unknown>
): AppSumoUpgradeSearch {
  const result: AppSumoUpgradeSearch = {};
  const plan = oneOf(
    typeof search.plan === 'string' ? search.plan.trim().toLowerCase() : '',
    PLAN_PARAMS
  );
  if (plan) result.plan = plan;
  const source = shortText(search.source);
  if (source) result.source = source;
  const feature = shortText(search.feature);
  if (feature) result.feature = feature;
  const status = oneOf(search.status, STATUSES);
  if (status) result.status = status;
  for (const key of UTM_KEYS) {
    const value = shortText(search[key]);
    if (value) result[key] = value;
  }
  return import.meta.env.DEV
    ? { ...result, ...parseAppSumoPreviewParams(search) }
    : result;
}

/** The plan wizard's search for a campaign link: the plan it opens on and
 *  where the link was, which the wizard's analytics keep. */
export type AppSumoWizardSearch = AppSumoPreviewParams & {
  plan?: AppSumoUpgradePlanKey;
  source: string;
};

/**
 * The search for an internal link to the plan wizard, which prices Starter
 * and Pro at the member price while the offer is open. It carries the dev
 * preview along, since TanStack drops params a link doesn't name and a
 * reviewer would otherwise land on the real (ineligible) offer.
 */
export function appSumoUpgradeSearch(link: {
  source: AppSumoUpgradeSource;
  plan?: AppSumoUpgradePlanKey;
}): AppSumoWizardSearch {
  return {
    ...(link.plan ? { plan: link.plan } : {}),
    source: `appsumo_${link.source}`,
    ...readAppSumoPreviewParams(),
  };
}

/** The same link as a plain href, for a CTA stored as a string. */
export function appSumoUpgradeHref(link: {
  source: AppSumoUpgradeSource;
  plan?: AppSumoUpgradePlanKey;
}): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(appSumoUpgradeSearch(link))) {
    if (value !== undefined) query.set(key, String(value));
  }
  return `/upgrade?${query.toString()}`;
}

/**
 * Where the old upgrade page sends a visitor: the plan wizard on the plan
 * the link named (else the one this tab last took to checkout), keeping the
 * link's source so an email's clicks stay apart. The utm params aren't
 * carried: the first pageview already recorded them.
 */
export function appSumoRedirectSearch(
  search: AppSumoUpgradeSearch,
  lastPlan?: AppSumoUpgradePlanKey
): AppSumoWizardSearch {
  const plan = search.plan ?? lastPlan;
  return {
    ...(plan ? { plan } : {}),
    source: search.source ?? 'appsumo_link',
    ...(import.meta.env.DEV ? parseAppSumoPreviewParams(search) : {}),
  };
}

// ---------------------------------------------------------------------------
// Copy (COPY.md, v2: Starter $19, Pro $39). Numbers come from the offer, so
// the words and the price at checkout can never drift apart.
// ---------------------------------------------------------------------------

export const SEE_YOUR_PRICE = 'See your price';
export const BOOK_A_CALL = 'Book a call with Sameer';

/** The server's booking link, if it is a plain https link (it becomes an
 *  href, where a `javascript:` value would run rather than navigate). */
export function safeCallUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' ? parsed.href : null;
  } catch {
    return null;
  }
}

/** The campaign's two plans, as the offer prices them. */
export interface AppSumoPlanPrices {
  pro: AppSumoUpgradePlan;
  starter: AppSumoUpgradePlan;
}

const isPriced = (
  plan: AppSumoUpgradePlan | undefined
): plan is AppSumoUpgradePlan =>
  !!plan &&
  Number.isInteger(plan.offer_cents) &&
  plan.offer_cents > 0 &&
  Number.isInteger(plan.list_cents) &&
  plan.list_cents > 0;

/**
 * Pro and Starter from the offer, or null when it lacks either (a contract
 * break, or an API older than the two plans): every surface then reads it as
 * no offer rather than word a price it doesn't have.
 */
export function appSumoPlanPrices(
  offer: Pick<AppSumoUpgradeOffer, 'plans'> | undefined
): AppSumoPlanPrices | null {
  const plans: unknown = offer?.plans;
  if (!Array.isArray(plans)) return null;
  const find = (key: AppSumoUpgradePlanKey) =>
    (plans as AppSumoUpgradePlan[]).find((plan) => plan?.key === key);
  const pro = find('pro');
  const starter = find('starter');
  return isPriced(pro) && isPriced(starter) ? { pro, starter } : null;
}

const price = (plan: AppSumoUpgradePlan) => formatCents(plan.offer_cents);
/** "$39 (regular $89)": a member price never goes without the regular one. */
const priceWithRegular = (plan: AppSumoUpgradePlan) =>
  `${price(plan)} (regular ${formatCents(plan.list_cents)})`;

/** "1,000": Starter's searches a month. The offer sends the number; billing's
 *  constant covers one that doesn't. */
const starterSearches = (
  starter: Pick<AppSumoUpgradePlan, 'search_limit'> | undefined
) => formatCount(starter?.search_limit ?? STARTER_MONTHLY_SEARCHES);
const offerStarter = (offer: Partial<Pick<AppSumoUpgradeOffer, 'plans'>>) =>
  offer.plans?.find((plan) => plan.key === 'starter');

/** Section 1: the bar at the top of every screen. */
export interface AppSumoBannerCopy {
  lead: string;
  rest: string;
  /** Open phase only: the final days can't be hidden. */
  dismissible: boolean;
}

export const BANNER_HIDE_LABEL = 'Hide for 3 days';

export function appSumoBannerCopy(
  prices: AppSumoPlanPrices,
  endsAt: string,
  phase: AppSumoUpgradePhase,
  now: number
): AppSumoBannerCopy {
  const deadline = deadlineLabels(endsAt);
  if (phase !== 'final_week') {
    return {
      lead: `Clu and Sequences from ${priceWithRegular(prices.starter)} a seat per month, until ${deadline.short}.`,
      rest: `That's ${BRAND.name} Starter at the AppSumo price, with ${starterSearches(prices.starter)} searches a month. ${BRAND.name} Pro is ${priceWithRegular(prices.pro)} and adds unlimited search and agents. Both go on top of your deal.`,
      dismissible: true,
    };
  }
  const days = calendarDaysUntil(endsAt, now);
  const lead =
    days <= 0
      ? `Your AppSumo price ends tonight at ${deadline.time} ET.`
      : days === 1
        ? 'Your AppSumo price ends tomorrow.'
        : `Your AppSumo price ends in ${days} days.`;
  return {
    lead,
    rest: `${BRAND.name} Starter ${priceWithRegular(prices.starter)} and Pro ${priceWithRegular(prices.pro)} a seat per month, for as long as you stay subscribed.`,
    dismissible: false,
  };
}

/** Section 2: the locked screens. */
export type AppSumoGateFeature =
  | 'clu'
  | 'sequences'
  | 'people'
  | 'companies'
  | 'generic';

export interface AppSumoGateCopy {
  title: string;
  line: string;
}

/** The directories' search allowance: Starter's, Pro's, and the deal's when
 *  its search is metered. */
const directorySearches = (
  prices: AppSumoPlanPrices,
  dealLimit: number | null | undefined
) =>
  `Starter includes ${starterSearches(prices.starter)} searches a month and Pro has no limit.${
    dealLimit == null
      ? ''
      : ` Your AppSumo deal includes ${formatCount(dealLimit)} a month.`
  }`;

/**
 * A screen's own title (a generic locked screen's), with the first plan it
 * names called "Cleanlist Starter" or "Cleanlist Pro": AppSumo's own listing
 * calls its tiers Starter and Pro (COPY.md rules). Naming both together,
 * "Cleanlist Starter and Pro" counts for both.
 */
export function withCleanlistPlanName(title: string): string {
  if (new RegExp(`\\b${BRAND.name} (Starter|Pro)\\b`).test(title)) return title;
  return title.replace(/\b(Starter|Pro)\b/, `${BRAND.name} $1`);
}

/**
 * The title and line of a locked screen while the offer is open. `generic`
 * keeps the screen's own title, with its plan named the Cleanlist way
 * ("Agents come with Cleanlist Pro"): those are Pro features, so its line is
 * Pro's.
 */
export function appSumoGateCopy(
  feature: AppSumoGateFeature,
  offer: Pick<AppSumoUpgradeOffer, 'ends_at' | 'keeps'>,
  prices: AppSumoPlanPrices,
  genericTitle = ''
): AppSumoGateCopy {
  switch (feature) {
    case 'clu':
      return {
        title: `Clu comes with ${BRAND.name} Starter and Pro`,
        line: "Describe who you want to reach. Clu finds them, builds the list and gets their emails. Clu isn't part of your AppSumo deal. Add either plan and you keep everything the deal gives you.",
      };
    case 'sequences':
      return {
        title: `Sequences come with ${BRAND.name} Starter and Pro`,
        line: "Send email from your own inbox, with call and manual tasks in the same sequence. A reply stops it. On Pro, Clu writes each email for one person. Your AppSumo deal doesn't include Sequences.",
      };
    case 'people':
      return {
        title: `The People directory comes with ${BRAND.name} Starter and Pro`,
        line: `Search people by title, company and location. ${directorySearches(prices, offer.keeps?.search_limit)}`,
      };
    case 'companies':
      return {
        title: `The Companies directory comes with ${BRAND.name} Starter and Pro`,
        line: `Search companies by industry, size and location. ${directorySearches(prices, offer.keeps?.search_limit)}`,
      };
    case 'generic':
      return {
        title: withCleanlistPlanName(genericTitle),
        line: `Add Pro on top of your AppSumo deal to use it. AppSumo customers pay ${priceWithRegular(prices.pro)} a seat per month until ${deadlineLabels(offer.ends_at).short}.`,
      };
  }
}

/** Every locked screen once the price has closed (or before it opens, or
 *  with the campaign off): a plan at the regular price, from the plans page. */
export const APPSUMO_GATE_CLOSED_LINE = `Add ${BRAND.name} Starter or Pro on top of your AppSumo deal to use it. Your deal stays as it is.`;

/** The price line under a locked screen's buttons. */
export function appSumoGatePriceLine(
  prices: AppSumoPlanPrices,
  endsAt: string
): string {
  return `AppSumo price: ${BRAND.name} Starter ${priceWithRegular(prices.starter)} or Pro ${priceWithRegular(prices.pro)} a seat per month, for as long as you stay subscribed. Offer ends ${deadlineLabels(endsAt).short}.`;
}

/** What a locked screen's `?feature=` points at on the upgrade page. */
export const GATE_FEATURE_GAIN: Partial<Record<string, AppSumoUpgradeGain>> = {
  clu: 'clu',
  agents: 'agents',
  sequences: 'sequences',
  search: 'search',
  people: 'search',
  companies: 'search',
  slack: 'slack',
  reports: 'reports',
  crm: 'crm',
  api: 'api',
};

/** Section 3, "What each plan adds": one row per key the server sends. */
export function appSumoGainCopy(
  gain: AppSumoUpgradeGain,
  offer: Pick<AppSumoUpgradeOffer, 'keeps'> &
    Partial<Pick<AppSumoUpgradeOffer, 'plans'>>
): AppSumoGateCopy {
  switch (gain) {
    case 'search': {
      const deal = offer.keeps?.search_limit;
      return {
        title: 'More People and Company search',
        line: `Starter includes ${starterSearches(offerStarter(offer))} searches a month and Pro has no limit.${
          deal == null
            ? ''
            : ` Your deal includes ${formatCount(deal)} a month.`
        }`,
      };
    }
    case 'clu':
      return {
        title: 'Clu',
        line: 'Describe who you want to reach. Clu finds them, builds the list and gets their emails.',
      };
    case 'sequences':
      return {
        title: 'Sequences',
        line: 'Email from your own inbox, plus call and manual tasks. A reply stops the sequence.',
      };
    case 'agents':
      return {
        title: 'Agents that run on their own',
        line: 'Up to 5 agents that run on a schedule and post their results to Slack.',
      };
    case 'slack':
      return {
        title: 'Clu in Slack',
        line: 'Ask Clu from Slack and approve credit spend there.',
      };
    case 'reports':
      return {
        title: 'Team reports',
        line: "Credits, enrichment, lists and each teammate's activity, in Reports.",
      };
    case 'crm':
      return {
        title: 'HubSpot and Salesforce',
        line: 'Push leads to your CRM and run enrichment inside it.',
      };
    case 'api':
      return {
        title: 'REST API',
        line: 'Run enrichment from your own tools with an API key.',
      };
  }
}

const GAIN_KEYS: Record<AppSumoUpgradeGain, true> = {
  search: true,
  clu: true,
  sequences: true,
  agents: true,
  slack: true,
  reports: true,
  crm: true,
  api: true,
};

const isGainKey = (key: unknown): key is AppSumoUpgradeGain =>
  typeof key === 'string' &&
  Object.prototype.hasOwnProperty.call(GAIN_KEYS, key);

const PLAN_KEYS: readonly AppSumoUpgradePlanKey[] = ['starter', 'pro'];

/** A gain the portal has words for, with the plans that include it. */
export interface AppSumoGain {
  key: AppSumoUpgradeGain;
  plans: AppSumoUpgradePlanKey[];
}

/** The gains the portal has words for, in the server's order. A key or a
 *  plan added server-side before its copy ships is left out rather than
 *  shown blank, and so is a row no plan includes. */
export function knownGains(
  rows: readonly AppSumoUpgradeGainRow[] | undefined
): AppSumoGain[] {
  return (Array.isArray(rows) ? rows : []).flatMap((row) => {
    // Read defensively: an API older than these rows sends bare strings.
    const key: unknown = row?.key;
    const listed: unknown = row?.plans;
    if (!isGainKey(key) || !Array.isArray(listed)) return [];
    const plans = PLAN_KEYS.filter((plan) => listed.includes(plan));
    return plans.length ? [{ key, plans }] : [];
  });
}

/** The gain a `?feature=` (or a locked screen) points at, if this offer has
 *  it. */
export function featureGain(
  gains: readonly AppSumoGain[],
  feature: string | undefined
): AppSumoUpgradeGain | null {
  const lead = feature ? GATE_FEATURE_GAIN[feature] : undefined;
  return lead && gains.some((gain) => gain.key === lead) ? lead : null;
}

/** A locked screen's tiles: the server's gains, the screen's own first. */
export function orderGains(
  rows: readonly AppSumoUpgradeGainRow[] | undefined,
  feature: string | undefined
): { gains: AppSumoGain[]; highlight: AppSumoUpgradeGain | null } {
  const gains = knownGains(rows);
  const highlight = featureGain(gains, feature);
  if (!highlight) return { gains, highlight };
  return {
    gains: [
      ...gains.filter((gain) => gain.key === highlight),
      ...gains.filter((gain) => gain.key !== highlight),
    ],
    highlight,
  };
}

export const PLAN_NAMES: Record<AppSumoUpgradePlanKey, string> = {
  pro: 'Pro',
  starter: 'Starter',
};

/** Each row's plan tag: "Starter and Pro", or "Pro". */
export function planTag(plans: readonly AppSumoUpgradePlanKey[]): string {
  if (plans.includes('starter') && plans.includes('pro')) {
    return 'Starter and Pro';
  }
  return plans.includes('pro') ? PLAN_NAMES.pro : PLAN_NAMES.starter;
}

/** Section 3: the upgrade page. */
export const UPGRADE_PAGE_TITLE =
  'Add unlimited search and Clu to your AppSumo deal';

/** Whether Pro adds CRM sync and the API for this tier (tiers 1 to 4, whose
 *  deal lacks them); the server only sends those rows then. */
export function addsCrmAndApi(
  rows: readonly AppSumoUpgradeGainRow[] | undefined
): boolean {
  const keys = new Set(knownGains(rows).map((gain) => gain.key));
  return keys.has('crm') && keys.has('api');
}

/** Pro's card opens with this, in bold. */
export const PRO_SEARCH_LEAD = 'Unlimited searches.';

/** The line under a plan card's price. */
export function planCardLine(
  plan: AppSumoUpgradePlanKey,
  withCrmAndApi: boolean,
  prices?: AppSumoPlanPrices
): string {
  if (plan === 'starter')
    return `Clu, Sequences and ${starterSearches(prices?.starter)} searches a month.`;
  return withCrmAndApi
    ? 'Everything in Starter, plus Clu writing your sequences, agents, Clu in Slack, team reports, CRM sync and the API.'
    : 'Everything in Starter, plus Clu writing your sequences, agents, Clu in Slack and team reports.';
}

// One seat reads "1 seat", not "1 seats".
const seatCount = (seats: number) =>
  `${formatCount(seats)} ${seats === 1 ? 'seat' : 'seats'}`;

/** "4 seats: $156 a month" */
export const totalLine = (seats: number, totalCents: number) =>
  `${seatCount(seats)}: ${formatCents(totalCents)} a month`;

export const GET_PLAN_BUTTON: Record<AppSumoUpgradePlanKey, string> = {
  pro: 'Get Pro',
  starter: 'Get Starter',
};

/** A member-price order's credits: the plan brings none, the deal's keep
 *  coming (`dealCredits` null when the offer doesn't say how many). */
export const creditsLine = (dealCredits: number | null, plan: string) =>
  `${
    dealCredits == null
      ? 'Your AppSumo credits keep coming.'
      : `Your AppSumo deal’s ${formatCount(dealCredits)} credits a month keep coming.`
  } ${plan} adds its features, not more credits.`;

export const trialLink = (days: number) => `Or try Pro free for ${days} days`;

/** The first charge is for every member: a trial starts at the member count
 *  whatever seats are picked. */
export const trialFinePrint = (firstChargeCents: number, chargeDate: string) =>
  `$0 today, card required. We charge ${formatCents(firstChargeCents)} plus tax on ${chargeDate} unless you cancel before then.`;

/** In place of the buttons for a member who isn't a workspace admin. */
export const ADMIN_ONLY_LINE =
  'Only a workspace admin can add a plan. Ask an admin to open this page.';

/** Checkout refused the trial (409 trial_unavailable); the link goes away. */
export const TRIAL_ALREADY_USED =
  "You've already had a Pro trial on this workspace.";

export const UPGRADE_FINE_PRINT =
  'The AppSumo price applies to every renewal while you stay subscribed. Cancel any time from Billing.';

export function deadlineLine(endsAt: string): string {
  const deadline = deadlineLabels(endsAt);
  return `Open to AppSumo customers until ${deadline.weekday}, ${deadline.long} at ${deadline.time} ET.`;
}

/** On Starter at the AppSumo price while it is open: in place of the plan
 *  cards. */
export const STARTER_STATE = {
  title: "You're on Starter",
  line: (prices: AppSumoPlanPrices) =>
    `Move to Pro for ${priceWithRegular(prices.pro)} a seat per month to take the search limit off and add Clu writing your sequences, agents, Clu in Slack and team reports.`,
  button: 'Move to Pro',
};

/** Section 3, feature tour: the gains that get a full block, each with a
 *  picture, its plan tag and three steps, in this order; the rest stay
 *  compact rows under "Also included". */
export const TOUR_GAINS = ['clu', 'sequences', 'agents'] as const;
export type TourGain = (typeof TOUR_GAINS)[number];

export const KEEPS_LINE =
  'If you cancel your plan, your account goes back to this deal.';

/** The deal's own terms, which a plan on top leaves alone. */
export function keepsRows(
  keeps: AppSumoUpgradeOffer['keeps'] | undefined
): string[] {
  if (!keeps) return ['Lifetime access'];
  return [
    `${formatCount(keeps.monthly_credits)} credits every month`,
    seatCount(keeps.seats),
    ...(keeps.leads_per_list == null
      ? []
      : [`Lists of up to ${formatCount(keeps.leads_per_list)} contacts`]),
    'Lifetime access',
  ];
}

export const LIMITS_HEADING = 'With Starter or Pro';

const limitValue = (value: number | null) =>
  value == null ? 'Unlimited' : formatCount(value);

/** The limits that move with a plan, in the deck's words. A key the deck has
 *  no words for is left out. */
export function limitRows(
  changes: AppSumoUpgradeOffer['limit_changes'] | undefined
): string[] {
  return (changes ?? []).flatMap((change) => {
    if (change.key === 'leads_per_list') {
      return [
        `Contacts per list: ${limitValue(change.current)} to ${limitValue(change.next)}`,
      ];
    }
    if (change.key === 'max_lists') {
      return [
        `Lists: ${limitValue(change.current)} to ${limitValue(change.next)}`,
      ];
    }
    return [];
  });
}

/** Section 3: Starter and Pro side by side, in the billing page's words.
 *  Only what the plans add: the deal stays as it is. */
export const INCLUDED_HEADING = "What's included";

/** A check (true), a dash (false), a value, or a value set in bold. */
export type IncludedCell = boolean | string | { text: string; strong: true };

export interface IncludedRow {
  label: string;
  starter: IncludedCell;
  pro: IncludedCell;
}

export interface IncludedGroup {
  title: string;
  rows: IncludedRow[];
}

/** The rows, from the offer: the gains the server sends for this tier (CRM
 *  sync and the API only to tiers 1 to 4, whose deal lacks them), the limits
 *  that move and Starter's searches. A group left with no rows is dropped. */
export function includedGroups(
  offer: Pick<AppSumoUpgradeOffer, 'gains' | 'limit_changes'>,
  prices: AppSumoPlanPrices
): IncludedGroup[] {
  const gains = knownGains(offer.gains);
  // A gain's row: each plan's value where the server says it has the gain.
  const gainRow = (
    key: AppSumoUpgradeGain,
    label: string,
    starter: IncludedCell = true,
    pro: IncludedCell = true
  ): IncludedRow[] => {
    const plans = gains.find((gain) => gain.key === key)?.plans;
    if (!plans) return [];
    return [
      {
        label,
        starter: plans.includes('starter') && starter,
        pro: plans.includes('pro') && pro,
      },
    ];
  };
  // Both plans move a limit to the same value.
  const limitRow = (key: string, label: string): IncludedRow[] =>
    (offer.limit_changes ?? []).flatMap((change) =>
      change.key === key
        ? [
            {
              label,
              starter: limitValue(change.next),
              pro: limitValue(change.next),
            },
          ]
        : []
    );
  const seatPrice = (plan: AppSumoUpgradePlan) =>
    `${price(plan)} (regular ${formatCents(plan.list_cents)})`;
  const groups: IncludedGroup[] = [
    {
      title: 'Price',
      rows: [
        {
          label: 'Price per seat, monthly',
          starter: seatPrice(prices.starter),
          pro: seatPrice(prices.pro),
        },
      ],
    },
    {
      title: 'Search and lists',
      rows: [
        {
          label: 'People and Company search',
          starter: `${starterSearches(prices.starter)} a month`,
          pro: { text: 'Unlimited', strong: true },
        },
        ...limitRow('leads_per_list', 'Contacts per list'),
        ...limitRow('max_lists', 'Lists'),
      ],
    },
    {
      title: 'Clu and agents',
      rows: [
        ...gainRow('clu', 'Clu'),
        ...gainRow('slack', 'Clu in Slack, with credit approvals'),
        ...gainRow('agents', 'Agents that run on their own', true, '5 active'),
      ],
    },
    {
      // No LinkedIn: the campaign never claims LinkedIn automation.
      title: 'Sequences',
      rows: [
        ...gainRow('sequences', 'Email sequences with call tasks'),
        ...gainRow('sequences', 'Clu writes each email for one person', false),
        ...gainRow('sequences', 'Mailboxes', '1 per seat', '1 per seat'),
      ],
    },
    {
      title: 'Reports, CRM and API',
      rows: [
        ...gainRow('reports', 'Team reports and analytics dashboard'),
        ...gainRow('crm', 'HubSpot and Salesforce sync'),
        ...gainRow('api', 'REST API access'),
      ],
    },
  ];
  return groups.filter((group) => group.rows.length > 0);
}

export function upgradeQuestions(
  offer: Pick<AppSumoUpgradeOffer, 'members'> &
    Partial<Pick<AppSumoUpgradeOffer, 'plans'>>
): { question: string; answer: string }[] {
  return [
    {
      question: 'What happens to my AppSumo deal?',
      answer:
        'Nothing. It keeps running on the same lifetime terms, and your plan sits on top of it. If you cancel the plan, you go back to your deal as it is today.',
    },
    {
      question: 'What happens to my credits?',
      answer:
        "Your deal's monthly credits keep coming. Starter and Pro at the AppSumo price add features, not credits. Enriching costs what it always has: 1 credit for a verified work email, 10 for a phone number, and a miss costs nothing.",
    },
    {
      question: "What's the difference between Starter and Pro?",
      answer: `Both add Clu and Sequences. Starter includes ${starterSearches(offerStarter(offer))} searches a month and Pro has no search limit. On Starter you write your sequence emails. On Pro, Clu writes them, and you also get agents that run on their own, Clu in Slack and team reports.`,
    },
    {
      question: 'How do seats work?',
      answer: `Both plans are priced per seat, one for each person in your workspace. You have ${formatCount(offer.members)} today. You can add seats later from Billing.`,
    },
    {
      question: 'How long does the price last?',
      answer:
        "For as long as you stay subscribed, on every renewal. If you cancel and come back later, it's the regular price.",
    },
    {
      question: 'How do I cancel?',
      answer:
        'From Billing, any time. Your plan runs to the end of the month you paid for, then your account goes back to your AppSumo deal.',
    },
  ];
}

/** Only for the trial screen after checkout, when neither the checkout this
 *  tab started nor the offer can say (COPY.md facts). */
export const APPSUMO_UPGRADE_TRIAL_DAYS = 7;

export const PAID_STATE = {
  title: (plan: string) => `You're on ${plan}`,
  line: (plan: string) =>
    `Your AppSumo deal is still here, with ${plan} on top. Your AppSumo price holds for as long as you stay subscribed.`,
};

export const TRIAL_STATE = {
  title: 'Your Pro trial is on',
  line: (days: number, charge?: { cents: number; date: string }) =>
    charge
      ? `You have ${days} days to try everything in Pro. We charge ${formatCents(charge.cents)} plus tax on ${charge.date} unless you cancel before then from Billing.`
      : `You have ${days} days to try everything in Pro.`,
};

/** The ways on from a finished checkout. */
export const DONE_LINKS = {
  clu: 'Open Clu',
  sequences: 'Set up Sequences',
  billing: 'Go to Billing',
};

/** A Sumoling already on a paid plan (outside the Starter-to-Pro case).
 *  Named by that plan, so the screen never claims the wrong one. */
export const ALREADY_ON_PRO_STATE = {
  title: (plan = 'Pro') => `You already have ${plan}`,
  line: (plan = 'Pro') =>
    `${plan} is on top of your AppSumo deal. Manage it from Billing.`,
};

export const PRICE_CLOSED_STATE = {
  title: (endsAt: string) =>
    `This price ended on ${deadlineLabels(endsAt).long}`,
  line: `You can still add ${BRAND.name} Starter or Pro on top of your AppSumo deal at the regular price.`,
};

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : undefined;

/**
 * A refusal's message. The API's error envelope arrives with its message under
 * `detail` (base-query's withLegacyDetail); a bare FastAPI body keeps the
 * raised dict, `{detail: {error, message}}`.
 */
export function apiErrorMessage(error: unknown, fallback: string): string {
  const data = asRecord(asRecord(error)?.data);
  if (typeof data?.detail === 'string' && data.detail) return data.detail;
  const raised = asRecord(data?.detail)?.message;
  if (typeof raised === 'string' && raised) return raised;
  if (typeof data?.message === 'string' && data.message) return data.message;
  return fallback;
}

/** A refusal's own code (`trial_unavailable`, `appsumo_admin_required`), from
 *  the envelope's details or a bare FastAPI body. */
export function apiErrorCode(error: unknown): string | undefined {
  const data = asRecord(asRecord(error)?.data);
  const code =
    asRecord(asRecord(data?.error)?.details)?.error ??
    asRecord(data?.detail)?.error;
  return typeof code === 'string' ? code : undefined;
}

/** The checkout couldn't start and the API said nothing more useful. */
export const CHECKOUT_ERROR_FALLBACK =
  'Could not open checkout. Please try again.';

/**
 * Where a switch the server already made (Starter to Pro, `action:
 * 'modified'`) sends the browser: its own `url` when that is this app or
 * https, else the upgrade page's paid screen. A `javascript:` or `data:` url
 * never becomes a navigation.
 */
export function modifiedDestination(
  url: string | null | undefined,
  origin: string
): string {
  const fallback = `${APPSUMO_UPGRADE_PATH}?status=success`;
  if (!url) return fallback;
  try {
    const parsed = new URL(url, origin);
    return parsed.origin === origin || parsed.protocol === 'https:'
      ? parsed.href
      : fallback;
  } catch {
    return fallback;
  }
}

/** Section 4: under the current plan's price on Plans, once bought. */
export const APPSUMO_PLAN_CAPTION = 'AppSumo price';

/** Section 4: the note at the top of Plans. */
export function appSumoPlansNote(
  prices: AppSumoPlanPrices,
  endsAt: string
): string {
  return `As an AppSumo customer you can add ${BRAND.name} Starter for ${priceWithRegular(prices.starter)} or ${BRAND.name} Pro for ${priceWithRegular(prices.pro)} a seat per month until ${deadlineLabels(endsAt).short}. Your deal stays as it is.`;
}

/** Section 4: the What's new Clu card's button, for an open offer. */
export const WHATS_NEW_CLU_ANNOUNCEMENT_ID = 'clu-launch-v1';
export const whatsNewCluCtaLabel = (prices: AppSumoPlanPrices) =>
  `Get Clu from ${priceWithRegular(
    prices.pro.offer_cents < prices.starter.offer_cents
      ? prices.pro
      : prices.starter
  )}`;

/** Section 4: the card above the classic home, while the price is open. */
export const HOME_SHOWCASE = {
  title: `${BRAND.name} does a lot more now than when you got your deal`,
  line: (prices: AppSumoPlanPrices, endsAt: string) =>
    `${BRAND.name} Starter and Pro add Clu and Sequences on top of your AppSumo deal, and Pro takes the search limit off. AppSumo customers pay ${priceWithRegular(prices.starter)} or ${priceWithRegular(prices.pro)} a seat per month until ${deadlineLabels(endsAt).short}.`,
  cards: [
    {
      key: 'clu',
      title: 'Clu',
      line: 'Describe who you want to reach. Clu finds them, builds the list and gets their emails.',
    },
    {
      key: 'agents',
      title: 'Agents',
      tag: PLAN_NAMES.pro,
      line: 'Describe a job once, like finding 50 new leads every Monday. It runs on its own and posts what it did to Slack.',
    },
    {
      key: 'sequences',
      title: 'Sequences',
      line: 'Send from your own inbox, with call and manual tasks. A reply stops it. On Pro, Clu writes each email for one person.',
    },
  ] as const satisfies readonly {
    key: TourGain;
    title: string;
    line: string;
    tag?: string;
  }[],
  primary: SEE_YOUR_PRICE,
  hide: 'Hide for a week',
};

// ---------------------------------------------------------------------------
// Section 5: the member price on Plans & billing and in the plan wizard,
// the one place Starter and Pro are bought. Every member price stands next
// to the regular one.
// ---------------------------------------------------------------------------

export const MEMBER_PRICE_LABEL = 'AppSumo member price';

/** The current-plan card on Plans & billing. The price stays while the
 *  plan does; the deadline is the last day to buy at it. */
export function memberPriceLine(
  prices: AppSumoPlanPrices,
  endsAt: string
): string {
  return `${MEMBER_PRICE_LABEL}: Starter ${priceWithRegular(prices.starter)}, Pro ${priceWithRegular(prices.pro)} a seat a month, for as long as you stay subscribed. Offer ends ${deadlineLabels(endsAt).short}.`;
}

/** The wizard's line under "Choose your plan". */
export const memberPlanSubline = (endsAt: string) =>
  `${MEMBER_PRICE_LABEL} for as long as you stay subscribed. Offer ends ${deadlineLabels(endsAt).short}.`;

/** The saving on a seat, under the price. */
export const memberSavingLine = (cents: number) =>
  `Save ${formatCents(cents)} a seat a month`;

/** The regular price beside a member price on the receipt. */
export const memberRegularHint = (cents: number) =>
  `regular ${formatCents(cents)}`;

// ---------------------------------------------------------------------------
// Analytics (README "PostHog events")
// ---------------------------------------------------------------------------

type Tier = number | null;

export type AppSumoUpgradeEvents = {
  appsumo_upgrade_banner_viewed: {
    tier: Tier;
    phase: AppSumoUpgradePhase;
    members: number;
  };
  appsumo_upgrade_banner_clicked: { tier: Tier; phase: AppSumoUpgradePhase };
  appsumo_upgrade_banner_dismissed: { tier: Tier };
  appsumo_upgrade_gate_viewed: {
    feature: AppSumoGateFeature;
    tier: Tier;
    phase: AppSumoUpgradePhase | null;
  };
  appsumo_upgrade_gate_clicked: { feature: AppSumoGateFeature; tier: Tier };
  appsumo_upgrade_page_viewed: {
    source: string | null;
    feature: string | null;
    tier: Tier;
    phase: AppSumoUpgradePhase | null;
    members: number | null;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_content?: string;
    utm_term?: string;
  };
  /** `seats` is null for a move from Starter, which keeps its seats. */
  appsumo_upgrade_checkout_started: {
    plan: AppSumoUpgradePlanKey;
    seats: number | null;
    trial: boolean;
    tier: Tier;
  };
  appsumo_upgrade_checkout_completed: {
    plan: AppSumoUpgradePlanKey | null;
    trial: boolean;
    tier: Tier;
  };
  appsumo_upgrade_call_clicked: { source: string; tier: Tier };
  appsumo_upgrade_home_viewed: {
    tier: Tier;
    phase: AppSumoUpgradePhase | null;
  };
  appsumo_upgrade_home_clicked: {
    tier: Tier;
    phase: AppSumoUpgradePhase | null;
  };
  appsumo_upgrade_home_hidden: {
    tier: Tier;
    phase: AppSumoUpgradePhase | null;
  };
};

/** Capture a campaign event; telemetry never takes a click down with it. */
export function trackAppSumoUpgrade<E extends keyof AppSumoUpgradeEvents>(
  posthog: Pick<PostHog, 'capture'> | null | undefined,
  event: E,
  properties: AppSumoUpgradeEvents[E]
): void {
  bestEffortAnalytics(() => {
    posthog?.capture(event, properties);
  });
}
